import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { CodeRepository } from '@data-layer/repositories';
import { CustomValidator, HTS_MASK } from '@helpers/custom-validator';
import { of } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { HtsNumberModel } from '@data-layer/models/common';

@Component({
  selector: 'app-isf-hts-control',
  templateUrl: './isf-hts-control.component.html',
  styleUrls: ['./isf-hts-control.component.scss'],
})
export class IsfHtsControlComponent implements OnChanges {
  @Input() public fc: UntypedFormControl;
  @Input() public isShowLabel: boolean = false;
  @Input() public isShowPlaceholder: boolean = false;
  @Input() public isShowHelper: boolean = true;

  public validationLoading: boolean = false;
  public mask = HTS_MASK;
  public errors: IErrorForm = this.createErrors();

  constructor(private codeRepository: CodeRepository, private changeDetector: ChangeDetectorRef) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.fc && this.fc) {
      this.updateFcValidators();
    }
  }

  private updateFcValidators() {
    this.fc.clearValidators();
    this.fc.clearAsyncValidators();

    this.fc.setValidators([CustomValidator.matchLength([6, 8, 10]), CustomValidator.requiredHTSCountryOfOrigin('countryOfOriginId')]);
    this.fc.setAsyncValidators([this.validateHTSNumber.bind(this)]);
  }

  private validateHTSNumber(control: AbstractControl) {
    const value: string = control.value;
    if (!value || ![6, 8, 10].includes(value.length)) {
      return of(null);
    }
    this.validationLoading = true;
    return this.codeRepository.searchHts(value).pipe(
      switchMap((res: HtsNumberModel[]) => {
        if (res.length === 0 && value.length > 6) {
          return this.codeRepository.searchHts(value.slice(0, 6)).pipe(
            map((list: HtsNumberModel[]) => ({
              valid: false,
              value: value.slice(0, 6),
              message: list.length > 0 ? 'notMatchInternationalHtsNumber' : 'notMatchHtsNumber',
            })),
          );
        }
        if (res.length === 0) {
          return of({ valid: false, value: value, message: 'notMatchHtsNumber' });
        }
        return of({ valid: true });
      }),
      map((data: { valid: boolean; value?: string; message?: string }) => {
        if (data.valid) {
          return null;
        }
        return { [data.message]: data.value };
      }),
      finalize(() => {
        this.validationLoading = false;

        this.changeDetector.detectChanges();
      }),
    );
  }

  private createErrors(): IErrorForm {
    return {
      required: 'FORM_ERROR.REQUIRED',
      notMatchInternationalHtsNumber: 'FORM_ERROR.NOT_MATCH_INTERNATIONAL_HTS_NUMBER',
      notMatchHtsNumber: 'FORM_ERROR.NOT_MATCH_HTS_NUMBER',
      matchLength: 'FORM_ERROR.SHOULD_MATCH_HTS_LENGTH',
    };
  }
}
